/* eslint-disable eqeqeq */
import { ChangeEvent, useMemo, useTransition } from 'react';

import { LCPhuketDistrict } from '@/lib/entities/domain';
import { RentCatalogArrayQueryParam } from '@/lib/entities/rent';
import { useRentCatalogQuery } from '@/lib/entities/rent/lib/use-rent-catalog-query';
import { useSaleCatalogQuery } from '@/lib/entities/sale/lib';
import { SaleCatalogArrayQueryParam } from '@/lib/entities/sale/model';

import { Checkbox } from '@/lib/shared/components/Input';
import { IValueLabel } from '@/lib/shared/components/Selects/model/value-label';

import { clsx } from 'clsx';

type ICatalogCheckboxBlock = {
  items: IValueLabel[];
  isTwoColumns?: boolean;
  isOneColumn?: boolean;
  isButton?: boolean;
  isHidden?: boolean;
  selectAllCheckbox?: {
    label: string;
  };
  className?: string;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: (id: string) => void;
} & (
  | {
      catalogQuery: SaleCatalogArrayQueryParam;
      type: 'sale';
    }
  | {
      catalogQuery: RentCatalogArrayQueryParam;
      type: 'rent';
    }
);

const selectSubRegions = (
  id: string,
  values: LCPhuketDistrict[],
  items: IValueLabel[],
  remove?: boolean
) => {
  const disabledState: Partial<Record<LCPhuketDistrict, boolean>> =
    items?.reduce((a: Partial<Record<LCPhuketDistrict, boolean>>, b) => {
      a[b.value as LCPhuketDistrict] = !!b.isDisabled;
      return a;
    }, {});
  if (id === 'bang-tao') {
    if (remove) {
      return values;
    } else {
      (['lagoon', 'layan'] as LCPhuketDistrict[]).forEach(
        (val: LCPhuketDistrict) => {
          if (!disabledState[val]) {
            values.push(val);
          }
        }
      );
    }
  } else if (id === 'karon') {
    if (remove) {
      return values;
    } else {
      (['kata-noi', 'kata'] as LCPhuketDistrict[]).forEach(
        (val: LCPhuketDistrict) => {
          if (!disabledState[val]) {
            values.push(val);
          }
        }
      );
    }
  } else if (id === 'rawai') {
    if (remove) {
      return values;
    } else {
      if (!disabledState['nai-harn']) {
        values.push('nai-harn');
      }
    }
  }
  return Array.from(new Set(values));
};

const numericQuery: (
  | RentCatalogArrayQueryParam
  | SaleCatalogArrayQueryParam
)[] = ['bedroomsAmount', 'bathroomAmount', 'beachDistance', 'priceButton'];

export const CheckboxBlock = ({
  catalogQuery,
  items,
  isTwoColumns,
  isOneColumn,
  isButton,
  isHidden,
  selectAllCheckbox,
  className,
  onMouseLeave,
  onMouseEnter,
  type,
}: ICatalogCheckboxBlock) => {
  const queryHook = type === 'rent' ? useRentCatalogQuery : useSaleCatalogQuery;
  const { setQuery, query } = queryHook();
  const [_, startTransition] = useTransition();
  const catalogQueryValue = query[catalogQuery as keyof typeof query] as (
    | string
    | number
  )[];

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const existingValues = catalogQueryValue
      ? ([...catalogQueryValue] as (string | number)[])
      : [];
    const value = numericQuery.includes(catalogQuery)
      ? Number(e.target.value)
      : e.target.value;
    let values: (string | number)[] = existingValues.includes(value)
      ? existingValues.filter((v) => v != value)
      : existingValues.concat(value);
    if (catalogQuery === 'phuketDistrict') {
      values = selectSubRegions(
        e.target.value,
        values as LCPhuketDistrict[],
        items,
        !e.target.checked
      );
    }
    startTransition(() => {
      setQuery({
        [catalogQuery]: values.filter((val) => val !== 'all'),
      });
    });
  };

  const isAllSelected = useMemo(
    () => catalogQueryValue.includes('all'),
    [catalogQueryValue]
  );

  const onSelectAll = () => {
    startTransition(() => {
      setQuery({
        [catalogQuery]: isAllSelected ? null : ['all'],
      });
    });
  };

  return (
    <form
      className={clsx(
        'flex flex-wrap gap-2.5 [&>label]:items-start lg:[&+button]:mt-5',
        {
          'grid grid-cols-1': isOneColumn,
          'grid grid-cols-2': isTwoColumns,
          hidden: isHidden,
        },
        className
      )}
    >
      {selectAllCheckbox && (
        <Checkbox
          label={selectAllCheckbox.label}
          value={'all'}
          className={clsx('mb-2.5', !isOneColumn && 'col-span-2')}
          isButton={isButton}
          theme="dark"
          onChange={onSelectAll}
          name="all"
          isChecked={isAllSelected}
          isPaleUncheckedLabel
          readOnly={false}
        />
      )}

      {items.map((item) => {
        const isChecked =
          catalogQueryValue.includes(item.value) ||
          (isAllSelected && !item.isDisabled);
        return (
          <Checkbox
            theme="dark"
            key={item.label + item.value}
            label={item.label[0].toUpperCase() + item.label.slice(1)}
            value={item.value}
            isButton={isButton}
            id={item.id}
            onChange={onChange}
            isChecked={isChecked}
            isPaleUncheckedLabel
            isDisabled={!!item.isDisabled && !isChecked}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            readOnly={false}
          />
        );
      })}
    </form>
  );
};
